import {
  avatar,
  background,
  captions,
  card,
  host,
  rule,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `EdgyLayout` (`layouts/EdgyLayout.tsx`): a card in the border colour holds
 * the speaker and captions; the episode and bars sit beside (landscape) or
 * below it. One title line and one subtitle line (three title lines in
 * landscape). Intended differences: the captions' line height is 1.1, not
 * v1's 1, which clipped the descenders of the last line (square keeps its
 * 1.28); in landscape they show six lines, not seven, and sit 80 px above
 * the card's foot, as they sit 80 px from its left edge (v1 centred seven
 * in the card's full height, leaving 35 px).
 */
const bars = { variant: 'thin' as const }
const handle = { size: 40, weight: 500, lineHeight: 1.2 }

export const edgyBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      card('portrait', 'card', [0, 0, 1042, 1420]),
      avatar('portrait', 60, 60, 70),
      {
        ...speaker('portrait', [171, 71, 700, 48], 'handle', handle, {
          surface: 'border',
        }),
        id: 'speaker',
      },
      captions('portrait', [60, 190, 922, 1170], 'center', {
        surface: 'border',
        lineHeight: 1.1,
      }),
      text('portrait', 'title', [60, 1477, 960, 68], {
        size: 40,
        weight: 500,
        lineHeight: 1.7,
      }),
      text('portrait', 'subtitle', [60, 1545, 960, 68], {
        size: 40,
        weight: 500,
        lineHeight: 1.7,
      }),
      rule('portrait', 'divider', [0, 1669, 1080, 2], 'border', 1),
      host('portrait', 60, 1730, 130, 'rect'),
      visualizer('portrait', [240, 1670, 780, 250], bars),
    ],
    square: [
      background(),
      card('square', 'card', [0, 0, 1042, 580]),
      avatar('square', 60, 60, 70),
      {
        ...speaker('square', [171, 71, 700, 48], 'handle', handle, {
          surface: 'border',
        }),
        id: 'speaker',
      },
      captions('square', [60, 190, 922, 330], 'center', { surface: 'border' }),
      text('square', 'title', [60, 637, 960, 68], {
        size: 40,
        weight: 500,
        lineHeight: 1.7,
      }),
      text('square', 'subtitle', [60, 705, 960, 68], {
        size: 40,
        weight: 500,
        lineHeight: 1.7,
      }),
      rule('square', 'divider', [0, 829, 1080, 2], 'border', 1),
      host('square', 60, 890, 130, 'rect'),
      visualizer('square', [240, 830, 780, 250], bars),
    ],
    landscape: [
      background(),
      card('landscape', 'bar', [0, 0, 1920, 53]),
      card('landscape', 'card', [674, 53, 1246, 977]),
      avatar('landscape', 754, 80, 70),
      {
        ...speaker('landscape', [865, 91, 700, 48], 'handle', handle, {
          surface: 'border',
        }),
        id: 'speaker',
      },
      captions('landscape', [754, 290, 1086, 660], 'center', {
        surface: 'border',
        lineHeight: 1.1,
        linesPerPage: 6,
      }),
      host('landscape', 60, 130, 130, 'rect'),
      text('landscape', 'title', [60, 310, 554, 168], {
        size: 40,
        weight: 500,
        lineHeight: 1.4,
      }),
      text('landscape', 'subtitle', [60, 478, 554, 56], {
        size: 40,
        weight: 500,
        lineHeight: 1.4,
      }),
      rule('landscape', 'divider', [0, 793, 674, 2], 'border', 1),
      visualizer('landscape', [50, 844, 574, 186], bars),
    ],
  },
}
